import { useContext, useState } from 'react';
import { Form, Input } from 'antd';
import { axiosInstance } from '../../../utils/axios';
import { useStep } from '../../../hooks/useStep';
import { validateEmail, validatePhoneNumber } from '../../../utils/validators';
import { useMessage } from '../../../hooks/useMessage';
import LoadingSpinner from '../util/LoadingSpinner';
import { ScreeningContentContext } from '../../../context/ScreeningContentProvider';
import ReferentForm from '../util/ReferentForm';
import { ErrorMessage } from '../../../shared/enums/error-message.enum';
import { useTranslation } from 'react-i18next';

interface ReferentFields {
  referents: [
    {
      name: string;
      email: number;
      phoneNumber: string;
    },
  ];
  allowContact: boolean;
}

function ReferentStep() {
  const { t } = useTranslation(['screening/landlord', 'screening/general']);
  const { toast } = useMessage();
  const { goToNextStep } = useStep();
  const [comment, setComment] = useState<string>('');

  const content = useContext(ScreeningContentContext);
  const loadingContent = content === 'loading';

  const getReferents = async () => {
    return axiosInstance.get('/screening/referent/getAll').then((response) => {
      const { referents, comment } = response.data;
      setComment(comment);
      if (referents.length === 0) return;
      const mappedReferents = referents.map((referent: any) => ({
        name: referent.name,
        email: referent.email,
        phoneNumber: referent.phoneNumber,
      }));
      const allowContact = referents[0].allowContact;
      return { referents: mappedReferents, allowContact };
    });
  };

  const handleSubmit = async (values: ReferentFields) => {
    axiosInstance
      .post('/screening/referent', values)
      .then(() => {
        toast.success({
          content: t('M-DataSaved', { ns: 'screening/general' }),
        });
        goToNextStep();
      })
      .catch((error) => {
        const { status, data } = error.response;
        if (
          status === 400 &&
          data.message === ErrorMessage.SELF_AS_REFERENT_NOT_ALLOWED
        ) {
          return toast.error({
            content: t('EM-SelfReferentNotAllowed'),
          });
        }
        toast.error({
          content: t('EM-General', { ns: 'screening/general' }),
        });
      });
  };

  return loadingContent ? (
    <LoadingSpinner />
  ) : (
    <ReferentForm
      title={content.landlordStatementCheck.title}
      description={content.landlordStatementCheck.description ?? ''}
      permission={content.landlordStatementCheck.permission}
      comment={comment}
      commentApiUrl={'screening/landlord-questions/comment'}
      handleSubmit={handleSubmit}
      getReferents={getReferents}
    >
      {(field) => (
        <>
          <Form.Item
            label={t('L-Name')}
            name={[field.name, 'name']}
            rules={[
              {
                required: true,
                message: t('EM-Name') ?? '',
              },
            ]}
          >
            <Input maxLength={2000} />
          </Form.Item>

          <Form.Item
            label={t('L-Email')}
            name={[field.name, 'email']}
            rules={[
              {
                required: true,
                message: t('EM-Email') ?? '',
              },
              { validator: validateEmail },
            ]}
          >
            <Input type="email" maxLength={2000} />
          </Form.Item>

          <Form.Item
            label={t('L-PhoneNumber')}
            name={[field.name, 'phoneNumber']}
            rules={[
              {
                required: true,
                message: t('EM-PhoneNumber') ?? '',
              },
              { validator: validatePhoneNumber },
            ]}
          >
            <Input type="phone" maxLength={50} />
          </Form.Item>
        </>
      )}
    </ReferentForm>
  );
}

export default ReferentStep;
